import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Sparkles, LogOut, LayoutDashboard, Shield } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { TIER_LABEL } from "../lib/tiers";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/packages", label: "Membership" },
  { to: "/zenergize-holistic-health-club", label: "The Club" },
  { to: "/trainings", label: "Trainings" },
  { to: "/shop", label: "Shop" },
  { to: "/events", label: "Events" },
  { to: "/chinese-astrology", label: "Astrology", external: false },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const doLogout = () => { logout(); navigate("/"); };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-brand-cream/90 border-b border-brand-line">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 flex items-center justify-between h-16">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <Sparkles size={20} className="text-brand-gold" />
          <span className="font-display text-2xl font-semibold text-brand-ink leading-none">Isoharts</span>
          <span className="hidden sm:block text-[10px] uppercase tracking-widest text-brand-muted leading-tight">International Soul<br />of Healing Arts</span>
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          {NAV.map((n) => (
            n.external ? (
              <a key={n.to} href={n.to} target="_blank" rel="noreferrer" data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-sm font-medium text-brand-inksoft hover:text-brand-sage transition-colors">
                {n.label}
              </a>
            ) : (
              <Link key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className="text-sm font-medium text-brand-inksoft hover:text-brand-sage transition-colors">
                {n.label}
              </Link>
            )
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          {user ? (
            <>
              {user.role === "admin" && (
                <Link to="/admin" className="btn-outline !px-4 !py-2 text-sm" data-testid="nav-admin">
                  <Shield size={16} /> Admin
                </Link>
              )}
              <Link to="/dashboard" className="btn-outline !px-4 !py-2 text-sm" data-testid="nav-dashboard">
                <LayoutDashboard size={16} /> {TIER_LABEL[user.tier]} Dashboard
              </Link>
              <button onClick={doLogout} className="text-brand-muted hover:text-brand-ink" data-testid="nav-logout" title="Log out">
                <LogOut size={18} />
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="text-sm font-medium text-brand-inksoft hover:text-brand-sage" data-testid="nav-login">Login</Link>
              <Link to="/signup" className="btn-primary !px-5 !py-2 text-sm" data-testid="nav-join-free">Join Free</Link>
            </>
          )}
        </div>

        <button className="lg:hidden text-brand-ink" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-brand-line bg-brand-cream px-5 py-4 space-y-3">
          {NAV.map((n) => (
            n.external ? (
              <a key={n.to} href={n.to} target="_blank" rel="noreferrer" onClick={() => setOpen(false)} className="block text-brand-inksoft font-medium">{n.label}</a>
            ) : (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="block text-brand-inksoft font-medium">{n.label}</Link>
            )
          ))}
          <div className="pt-3 border-t border-brand-line flex flex-col gap-2">
            {user ? (
              <>
                {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="btn-outline !py-2 text-sm">Admin</Link>}
                <Link to="/dashboard" onClick={() => setOpen(false)} className="btn-primary !py-2 text-sm">Dashboard</Link>
                <button onClick={() => { setOpen(false); doLogout(); }} className="btn-outline !py-2 text-sm">Log out</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="btn-outline !py-2 text-sm">Login</Link>
                <Link to="/signup" onClick={() => setOpen(false)} className="btn-primary !py-2 text-sm">Join Free</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
